import type { Account, AuthService } from '$lib/services/auth';

/**
 * What the app inside the sign-in gate gets: who is signed in, a way to sign out, and a way
 * to make the sign-in current before a request.
 */
export interface SignedIn {
  readonly email: string;
  signOut(): void;
  renew(): Promise<void>;
  expired(): void;
}

export type AuthView = 'checking' | 'signIn' | 'reset' | 'invite' | 'recovery' | 'signedIn';

/** The path a signed-out visitor lands on, such as from an MCP client's sign-in. */
export const SIGN_IN_PATH = '/signin';

/**
 * Only a path on this site may be returned to after sign-in; anything else, such as another
 * host or `//evil.example`, would make this an open redirect.
 */
export function safeReturn(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    return null;
  }
  return value;
}

/**
 * Who is signed in, and which sign-in screen to show. A link from a sign-in email (invite or
 * password recovery) is handled first; after sign-in, a `?return=` path on this site is
 * followed, which is how an MCP client's sign-in gets back to its consent page.
 */
export class AuthSession {
  view = $state<AuthView>('checking');
  account = $state<Account | null>(null);
  error = $state<string | null>(null);
  notice = $state<string | null>(null);
  busy = $state(false);

  private inviteToken: string | null = null;

  constructor(
    private readonly service: AuthService,
    private readonly navigate: (path: string) => void = (path) => location.assign(path),
    private readonly returnTo: string | null = typeof location === 'undefined'
      ? null
      : safeReturn(new URL(location.href).searchParams.get('return')),
  ) {}

  async start(): Promise<void> {
    await this.attempt(async () => {
      const callback = await this.service.callback();
      if (callback?.kind === 'invite') {
        this.inviteToken = callback.token;
        this.view = 'invite';
        return;
      }
      if (callback?.kind === 'recovery') {
        this.account = await this.service.current();
        this.view = 'recovery';
        return;
      }
      this.arrive(await this.service.current());
    });
  }

  async signIn(email: string, password: string): Promise<void> {
    await this.attempt(async () => this.arrive(await this.service.signIn(email, password)));
  }

  async signOut(): Promise<void> {
    // Identity can refuse a sign-in it no longer honors; the person is signed out regardless.
    await this.service.signOut().catch(() => {});
    this.account = null;
    this.error = null;
    this.view = 'signIn';
  }

  /** The server refused the sign-in, which renewing could not save. */
  async expire(): Promise<void> {
    await this.signOut();
    this.notice = 'Your sign-in has expired; sign in again';
  }

  async requestReset(email: string): Promise<void> {
    await this.attempt(async () => {
      await this.service.requestReset(email);
      this.notice = `If ${email} has an account, a reset link is on its way.`;
      this.view = 'signIn';
    });
  }

  async acceptInvite(password: string): Promise<void> {
    const token = this.inviteToken;
    if (!token) return;
    await this.attempt(async () => this.arrive(await this.service.acceptInvite(token, password)));
  }

  async setPassword(password: string): Promise<void> {
    await this.attempt(async () => {
      await this.service.setPassword(password);
      this.arrive(this.account ?? (await this.service.current()));
    });
  }

  show(view: 'signIn' | 'reset'): void {
    this.error = null;
    this.notice = null;
    this.view = view;
  }

  private arrive(account: Account | null): void {
    this.account = account;
    if (!account) {
      this.view = 'signIn';
      return;
    }
    if (this.returnTo) {
      this.navigate(this.returnTo);
      return;
    }
    // Signed in at the sign-in address with nowhere to return to: show the app at its own.
    if (typeof location !== 'undefined' && location.pathname === SIGN_IN_PATH) {
      history.replaceState(history.state, '', '/');
    }
    this.view = 'signedIn';
  }

  private async attempt(run: () => Promise<void>): Promise<void> {
    this.busy = true;
    this.error = null;
    try {
      await run();
    } catch (error) {
      this.error = error instanceof Error ? error.message : String(error);
      if (this.view === 'checking') this.view = 'signIn';
    } finally {
      this.busy = false;
    }
  }
}
